/**
 * One builder per route.
 *
 * These are the single source of what any page claims about itself. A React
 * page calls the builder for the thing it is showing; the prerenderer calls
 * `metaForPath` and gets the identical object. Nothing about a page's title,
 * description or structured data is written twice.
 *
 * Descriptions are assembled from curriculum prose — a lesson's summary, a
 * concept's summary, a course's description — never from a template with the
 * title slotted into it. "Learn Adding Fractions at Yuan Math" is the kind of
 * description that makes a result look automated, and it says nothing a title
 * has not already said.
 */

import { BRAND, PRICING } from '@yuan-math/config';
import type { Concept, Course, CurriculumRegistry, Lesson } from '@yuan-math/curriculum';
import type { JsonLd, MetaLink, PageMeta } from './types.ts';
import { SITE } from './site.ts';
import { conjoin, describe, flatten, truncate, withBrand } from './text.ts';
import {
  asDocument,
  breadcrumbNode,
  conceptNode,
  courseNode,
  itemListNode,
  lessonNode,
  offerNode,
  organizationNode,
  webSiteNode,
} from './jsonld.ts';

/** Fills the parts of a `PageMeta` that are almost always the same. */
function page(meta: Omit<PageMeta, 'robots' | 'kind' | 'lead' | 'links' | 'jsonLd'> & Partial<PageMeta>): PageMeta {
  return {
    robots: 'index',
    kind: 'website',
    lead: meta.description,
    links: [],
    jsonLd: [],
    ...meta,
    title: withBrand(meta.title),
  };
}

/** The trail every content page starts from. */
const HOME_CRUMB: MetaLink = { label: SITE.name, path: '/' };

function withContext(nodes: JsonLd[]): JsonLd[] {
  return nodes.map(asDocument);
}

/* -------------------------------------------------------------------------- */
/* Public pages                                                               */
/* -------------------------------------------------------------------------- */

export function homeMeta(registry: CurriculumRegistry, origin: string = SITE.origin): PageMeta {
  const available = registry.availableConcepts().length;
  const total = registry.concepts.length;
  const courses = registry.curriculum.courses.filter((course) => course.status === 'available');

  return page({
    title: `${BRAND.name} — ${BRAND.tagline}`,
    description: describe(
      BRAND.heroSubheadline,
      `${available} concepts ready to study out of ${total} mapped, free`,
    ),
    path: '/',
    heading: BRAND.heroHeadline,
    lead: BRAND.heroSubheadline,
    links: [
      { label: 'Courses', path: '/courses' },
      { label: 'The Math Map', path: '/map' },
      { label: 'Find your level', path: '/diagnostic' },
      ...courses.slice(0, 8).map((course) => ({ label: course.title, path: `/courses/${course.id}` })),
    ],
    jsonLd: withContext([organizationNode(origin), webSiteNode(origin)]),
  });
}

export function coursesMeta(registry: CurriculumRegistry, origin: string = SITE.origin): PageMeta {
  const courses = registry.curriculum.courses.filter((course) => course.status === 'available');
  const links = courses.map((course) => ({ label: course.title, path: `/courses/${course.id}` }));

  return page({
    title: 'Mathematics courses, kindergarten to university',
    description: describe(
      `Every published course at ${BRAND.name}, from Grade 3 arithmetic to Precalculus`,
      'each one a route through the same map of concepts',
    ),
    path: '/courses',
    heading: 'Courses',
    lead: 'A course is a familiar, grade-shaped route through concepts that are themselves grade-independent.',
    links,
    jsonLd: withContext([
      itemListNode('Mathematics courses', links, origin),
      breadcrumbNode([HOME_CRUMB, { label: 'Courses', path: '/courses' }], origin),
    ]),
  });
}

export function courseMeta(
  registry: CurriculumRegistry,
  course: Course,
  origin: string = SITE.origin,
): PageMeta {
  const units = registry.unitsOfCourse(course.id);
  const lessons = registry.lessonsOfCourse(course.id);
  const level = registry.level(course.level)?.title ?? '';

  return page({
    title: course.title,
    description: describe(
      course.description,
      lessons.length > 0 ? `${lessons.length} lessons across ${units.length} units, free to read` : undefined,
    ),
    path: `/courses/${course.id}`,
    heading: course.title,
    lead: flatten(course.subtitle || course.description),
    links: lessons.map((lesson) => ({ label: lesson.title, path: `/lessons/${lesson.id}` })),
    jsonLd: withContext([
      courseNode(registry, course, origin),
      breadcrumbNode(
        [
          HOME_CRUMB,
          { label: 'Courses', path: '/courses' },
          { label: level || course.title, path: `/courses/${course.id}` },
        ],
        origin,
      ),
    ]),
  });
}

export function lessonMeta(
  registry: CurriculumRegistry,
  lesson: Lesson,
  origin: string = SITE.origin,
): PageMeta {
  const concept = registry.concept(lesson.conceptId);
  const course = registry.courseOfLesson(lesson.id);
  const { previous, next } = registry.neighbouringLessons(lesson.id);

  const links: MetaLink[] = [];
  if (concept) links.push({ label: concept.title, path: `/concepts/${concept.id}` });
  if (course) links.push({ label: course.title, path: `/courses/${course.id}` });
  if (previous) links.push({ label: `Previous: ${previous.title}`, path: `/lessons/${previous.id}` });
  if (next) links.push({ label: `Next: ${next.title}`, path: `/lessons/${next.id}` });

  const trail: MetaLink[] = [HOME_CRUMB, { label: 'Courses', path: '/courses' }];
  if (course) trail.push({ label: course.title, path: `/courses/${course.id}` });
  trail.push({ label: lesson.title, path: `/lessons/${lesson.id}` });

  return page({
    title: lesson.title,
    description: describe(lesson.summary, `A ${lesson.estimatedMinutes}-minute lesson, free to read`),
    path: `/lessons/${lesson.id}`,
    kind: 'article',
    heading: lesson.title,
    lead: flatten(lesson.summary),
    links,
    jsonLd: withContext([lessonNode(registry, lesson, origin), breadcrumbNode(trail, origin)]),
  });
}

export function conceptMeta(
  registry: CurriculumRegistry,
  concept: Concept,
  origin: string = SITE.origin,
): PageMeta {
  const strand = registry.strand(concept.strand);
  const lessons = registry.lessonsForConcept(concept.id);
  const prerequisites = concept.prerequisites
    .map((id) => registry.concept(id))
    .filter((item): item is Concept => item !== undefined);

  const links: MetaLink[] = [
    ...lessons.map((lesson) => ({ label: lesson.title, path: `/lessons/${lesson.id}` })),
    ...prerequisites.map((item) => ({ label: `Before this: ${item.title}`, path: `/concepts/${item.id}` })),
    { label: 'The Math Map', path: '/map' },
  ];

  const planned = concept.status === 'planned';

  return page({
    title: concept.title,
    description: describe(
      concept.summary,
      planned
        ? 'Mapped, with lessons still to be written'
        : prerequisites.length > 0
          ? `Builds on ${conjoin(prerequisites.slice(0, 3).map((item) => item.title.toLowerCase()))}`
          : undefined,
    ),
    path: `/concepts/${concept.id}`,
    heading: concept.title,
    lead: flatten(concept.summary),
    links,
    jsonLd: withContext([
      conceptNode(registry, concept, origin),
      breadcrumbNode(
        [
          HOME_CRUMB,
          { label: 'Math Map', path: '/map' },
          { label: strand?.title ?? concept.strand, path: '/map' },
          { label: concept.title, path: `/concepts/${concept.id}` },
        ],
        origin,
      ),
    ]),
  });
}

export function mapMeta(registry: CurriculumRegistry, origin: string = SITE.origin): PageMeta {
  const total = registry.concepts.length;
  const strands = registry.curriculum.strands;

  return page({
    title: 'The Math Map — how mathematics connects',
    description: describe(
      `All ${total} concepts in ${BRAND.name}, drawn as the graph they are`,
      'each one showing what must be understood first and what it unlocks',
    ),
    path: '/map',
    heading: 'The Math Map',
    lead: 'Mathematics is not a list of topics. It is a graph, and this is the graph.',
    links: registry
      .availableConcepts()
      .slice(0, 60)
      .map((concept) => ({ label: concept.title, path: `/concepts/${concept.id}` })),
    jsonLd: withContext([
      itemListNode(
        'Strands of mathematics',
        strands.map((strand) => ({ label: strand.title, path: '/map' })),
        origin,
      ),
      breadcrumbNode([HOME_CRUMB, { label: 'Math Map', path: '/map' }], origin),
    ]),
  });
}

export function diagnosticMeta(origin: string = SITE.origin): PageMeta {
  return page({
    title: 'Find your level — a free mathematics diagnostic',
    description: describe(
      'A short adaptive assessment that finds the highest point in the curriculum you are secure at',
      'and the gaps underneath it',
    ),
    path: '/diagnostic',
    heading: 'Find your level',
    lead: 'Answer a handful of questions and start where you actually are, not where a grade says you should be.',
    links: [
      { label: 'Courses', path: '/courses' },
      { label: 'The Math Map', path: '/map' },
    ],
    jsonLd: withContext([breadcrumbNode([HOME_CRUMB, { label: 'Diagnostic', path: '/diagnostic' }], origin)]),
  });
}

/**
 * The plans, priced from `@yuan-math/config` rather than from an argument.
 *
 * The pricing page and the structured data are then the same numbers by
 * construction: a markup price that disagrees with the page is the one
 * structured-data mistake that reaches a customer.
 */
function pricedPlans(): { name: string; price: number }[] {
  return Object.values(PRICING)
    .map((plan) => {
      // "Free" is a price of zero. "Custom" is not a price at all and must not
      // become one — a School tier marked up as $0 is a lie a crawler repeats,
      // and stripping the non-digits from it would produce exactly that.
      const digits = plan.price.replace(/[^0-9.]/g, '');
      if (digits !== '') return { name: plan.name, price: Number(digits) };
      return { name: plan.name, price: /free/i.test(plan.price) ? 0 : Number.NaN };
    })
    .filter((plan) => Number.isFinite(plan.price));
}

export function pricingMeta(origin: string = SITE.origin): PageMeta {
  return page({
    title: 'Pricing — every lesson is free',
    description: describe(
      'Lessons, the Math Map, mastery tracking and spaced review cost nothing at any tier',
      'a subscription lifts the daily cap on generated practice',
    ),
    path: '/pricing',
    heading: 'Pricing',
    lead: 'No mathematics is behind the paywall. Only the volume of generated practice is capped.',
    links: [
      { label: 'Start learning', path: '/courses' },
      { label: 'What we promise parents', path: '/privacy' },
    ],
    jsonLd: withContext([
      offerNode(pricedPlans(), origin),
      breadcrumbNode([HOME_CRUMB, { label: 'Pricing', path: '/pricing' }], origin),
    ]),
  });
}

export function aboutMeta(origin: string = SITE.origin): PageMeta {
  return page({
    title: 'About',
    description: describe(
      `Why ${BRAND.name} is a graph of mathematical concepts with a website on top`,
      'and what mastery has to mean before it is claimed',
    ),
    path: '/about',
    heading: `About ${BRAND.name}`,
    lead: 'A learning platform built on the structure of the subject rather than on a list of topics.',
    links: [
      { label: 'The Math Map', path: '/map' },
      { label: 'Courses', path: '/courses' },
      { label: 'Privacy', path: '/privacy' },
    ],
    jsonLd: withContext([
      organizationNode(origin),
      breadcrumbNode([HOME_CRUMB, { label: 'About', path: '/about' }], origin),
    ]),
  });
}

export function privacyMeta(origin: string = SITE.origin): PageMeta {
  return page({
    title: 'Privacy — no ads, no third-party analytics',
    description: describe(
      `What ${BRAND.name} stores, what it never stores, and what a parent or teacher can see`,
      'there is no advertising and no interest-based profiling, ever',
    ),
    path: '/privacy',
    heading: 'Privacy',
    lead: 'A promise made to parents, and enforced by the code rather than by policy alone.',
    links: [{ label: 'About', path: '/about' }],
    jsonLd: withContext([breadcrumbNode([HOME_CRUMB, { label: 'Privacy', path: '/privacy' }], origin)]),
  });
}

/* -------------------------------------------------------------------------- */
/* Pages that must not be indexed                                             */
/* -------------------------------------------------------------------------- */

/**
 * A page belonging to one student.
 *
 * Everything a learner profile writes to is `noindex`, and so is every page
 * that only makes sense mid-flow — sign-in, a password reset carrying a token,
 * a generated practice set. None of them are useful search results, all of them
 * are near-duplicates of one another, and a practice page indexed today shows a
 * different problem tomorrow.
 *
 * These still get a title. A tab reading "Yuan Math" four times over is a small
 * cruelty, and browser history is written from the title too.
 */
export function privateMeta(options: { title: string; path: string; description?: string }): PageMeta {
  return page({
    title: options.title,
    description: truncate(options.description ?? `${options.title} on ${BRAND.name}.`, 158),
    path: options.path,
    robots: 'noindex',
    heading: options.title,
  });
}

export function practiceMeta(registry: CurriculumRegistry, conceptId: string): PageMeta {
  const concept = registry.concept(conceptId);
  const title = concept ? `Practice: ${concept.title}` : 'Practice';
  return privateMeta({
    title,
    path: `/practice/${conceptId}`,
    description: concept
      ? describe(`Generated practice for ${concept.title.toLowerCase()}`, concept.summary)
      : undefined,
  });
}

export function notFoundMeta(path = '/404'): PageMeta {
  return privateMeta({
    title: 'Page not found',
    path,
    description: 'That page does not exist. The Math Map and every course are still one click away.',
  });
}
